import {
  Flex,
  ScrollArea,
  SimpleGrid,
  Text,
  Modal,
  Button,
  Loader,
  Center,
} from "@mantine/core";
import { useState } from "react";
import Search from "../Search";
import { BadgeInstruments } from "../Badge";
import { useMediaQuery } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import {
  IQueryInstruments,
  useInfiniteInstruments,
} from "../../hooks/useInstruments";

type IProps = {
  idProduct: number | undefined;
  isOpen: boolean;
  onClose: () => void;
  isCripto: boolean;
};

export default function InstrumentsModal({
  idProduct,
  isOpen,
  onClose,
  isCripto = false,
}: IProps) {
  const isLargerThan991 = useMediaQuery("(max-width: 991px)");
  const { t } = useTranslation();
  const [query, setQuery] = useState<IQueryInstruments>({
    page: 1,
    symbol: "",
    idProduct,
  });
  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } =
    useInfiniteInstruments(query);

  return (
    <Modal
      size="all"
      opened={isOpen}
      onClose={onClose}
      transitionProps={{
        exitDuration: 600,
        transition: "slide-up",
        duration: 600,
        timingFunction: "ease",
      }}
      withCloseButton={false}
      centered
      scrollAreaComponent={ScrollArea.Autosize}
      styles={{
        content: {
          borderRadius: "12px",
          minWidth: "fit-content",
          maxWidth: "fit-content",
        },
        inner: {
          zIndex: 1000,
        },
        overlay: { zIndex: 999 },
        header: {
          marginBottom: "0",
          background: "#F0F0F0",
          padding: "20px 20px",
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          borderBottom: "1px solid #D7D7D7",
          gap: "24px",
          minWidth: "100%",
        },
        root: {
          minWidth: "100%",
          maxWidth: "800px",
        },
        close: {
          display: "flex",
          height: "28px",
          width: "28px",
          justifyContent: "center",
          alignItems: "center",
          svg: {
            height: "24px !important",
            width: "24px !important",
          },
        },
        body: {
          width: "90vw",
          minWidth: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          backgroundColor: "#fff",
          borderRadius: "12px",
          padding: "0",
          maxWidth: "800px",
        },
      }}
    >
      <Modal.Header>
        <Flex direction={"column"} w="100%" gap="16px">
          <Flex justify={"space-between"} align={"center"} w="100%" gap="24px">
            <Text
              style={{
                color: "#2F2F2F",
                fontSize: "18px",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              {isCripto
                ? t("global.instruments.label_crypto")
                : t("global.instruments.label")}
            </Text>
            <Flex justify={"space-between"} align={"center"} w="fit" gap="24px">
              {isLargerThan991 ? null : (
                <Search
                  placeholder={t("global.instruments.placeholder_search")}
                  value={query.symbol}
                  onChange={(e) =>
                    setQuery((prev) => ({ ...prev, symbol: e.target.value }))
                  }
                  onClick={() => setQuery((prev) => ({ ...prev, symbol: "" }))}
                />
              )}
              <Modal.CloseButton />
            </Flex>
          </Flex>
          {isLargerThan991 ? (
            <Search
              placeholder={t("global.instruments.placeholder_search")}
              value={query.symbol}
              onChange={(e) =>
                setQuery((prev) => ({ ...prev, symbol: e.target.value }))
              }
              onClick={() => setQuery((prev) => ({ ...prev, symbol: "" }))}
            />
          ) : null}
        </Flex>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <Center py="80px">
            <Loader size={25} color="blue" />
          </Center>
        ) : (
          <Flex align="center" direction={"column"} mb={24}>
            <SimpleGrid
              p="15px 20px"
              breakpoints={[
                { maxWidth: 991, cols: 6 },
                { maxWidth: 800, cols: 5 },
                { maxWidth: 700, cols: 4 },
                { maxWidth: 600, cols: 3 },
                { maxWidth: 450, cols: 2 },
                { maxWidth: 300, cols: 1 },
              ]}
              w="100%"
              spacing={"16px"}
              cols={6}
            >
              {data?.pages.map((page) =>
                page.data.map((instrument, index) => (
                  <BadgeInstruments
                    key={`${instrument}${index}`}
                    badgeName={`${instrument}`}
                  />
                ))
              )}
            </SimpleGrid>
            {!!hasNextPage && (
              <Button
                w={254}
                variant="primaryTransparent"
                onClick={() => fetchNextPage()}
                loading={isFetchingNextPage}
                disabled={!hasNextPage}
              >
                {isFetchingNextPage
                  ? t("global.button.loading")
                  : t("global.button.loading_more")}
              </Button>
            )}
          </Flex>
        )}
      </Modal.Body>
    </Modal>
  );
}
